import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import { useDispatch } from 'react-redux';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import { CheckboxWithLabel, TextField } from 'formik-material-ui';
import { Button, CircularProgress } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import InputAdornment from '@material-ui/core/InputAdornment';
import * as yup from 'yup';
import isEmpty from 'lodash/isEmpty';
import has from 'lodash/has';
import PhoneInput from 'react-phone-number-input';
import clsx from 'clsx';

import { postRequest } from 'utils/api';
import { getTokenFromResponse, identifyFullStoryUser, setCrispUser } from 'utils/helpers';
import { signIn } from 'features/authSlice';
import { closeModal, openModal, setFooter, setRoleFilter } from 'features/utilsSlice';
import {
  saveAccessToken,
  getReferralCode,
  setLastSignInRole,
  getUuid,
  deleteUuid,
  getRedirectTo,
  setRedirectTo,
  setCurrentUserLS,
} from 'utils/localStorage';
import { trackAhoyEvent, updateAhoyToken } from 'utils/ahoy';
import Email from 'public/icons/form/email.svg';
import User from 'public/icons/form/user.svg';
import AnimateListItem from 'components/animations/AnimateListItem';
import AnimateList from 'components/animations/AnimateList';
import PasswordConfirmation from 'components/PasswordConfirmation';
import Switch from 'components/Switch';
import styles from 'styles/components/auth/AuthForm.module.scss';

const defaultRequired = 'is required.';
const width = { width: '100%' };

const schema = yup.object({
  email: yup
    .string()
    .email('is not a valid email')
    .required('is required'),
  display_name: yup.string().required(defaultRequired),
  contact_phone: yup.number().required(defaultRequired),
  terms_of_service: yup
    .bool()
    .oneOf(
      [true],
      'You must agree to the Terms of Service to use our site. This includes our Privacy Policy, Cookie Policy, and Acceptable Use Policy.'
    ),
  password: yup
    .string()
    .required(defaultRequired)
    .min(6, 'Must contain at least 6 characters'),
  password_confirmation: yup
    .string()
    .required(defaultRequired)
    .oneOf([yup.ref('password')], "Passwords don't match"),
});

const initialValues = {
  email: '',
  display_name: '',
  password: '',
  password_confirmation: '',
  contact_phone: '',
  contact_phone_ext: '',
  terms_of_service: false,
};

const SignUpForm = ({
  type = 'advertiser',
  sendToken = false,
  isDialog = false,
  agency = false,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [adult, setAdult] = useState('nothing-selected');
  const dispatch = useDispatch();
  const router = useRouter();
  const theme = useTheme();
  const isAffiliate = type === 'affiliate';
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));

  const handleSubmit = (values, actions) => {
    setIsLoading(true);
    const refCode = getReferralCode();
    const uuid = getUuid();
    const redirectTo = getRedirectTo();

    postRequest({
      endpoint: 'sign_up',
      data: {
        user: {
          ...values,
          ref_code: refCode,
          role: isAffiliate ? 'affiliate' : 'advertiser',
          primary_interest: isAffiliate ? 'sell' : 'buy',
          agency,
          adult,
        },
        uuid,
      },
      successMessage: (uuid && !isDialog) || !uuid ? 'Successfully signed up.' : '',
      version: null,
      config: {
        headers: {
          'Content-Type': 'application/json',
        },
        sendToken,
      },
      ...actions,
    })
      .then(response => {
        const token = getTokenFromResponse(response);

        if (token) saveAccessToken(token);

        if (uuid) deleteUuid();

        if (uuid && isDialog) {
          dispatch(closeModal('Which Are You'));
          dispatch(openModal({ name: 'Full Screen' }));
        }

        dispatch(signIn(response.data));
        setCurrentUserLS(response.data);
        dispatch(setRoleFilter('advertiser'));
        setCrispUser(response.data);
        identifyFullStoryUser(response.data);

        if (token) updateAhoyToken(token);
        trackAhoyEvent('Sign Up', { role: response.data.role, id: response.data.id });

        setLastSignInRole('advertiser');
        setIsLoading(false);

        if (uuid && isDialog) return;

        if (uuid && !isDialog && redirectTo) {
          router.push(redirectTo);
          if (redirectTo) setRedirectTo(null);
        }

        router.push('/access-confirmation');
      })
      .catch(() => setIsLoading(false));
  };

  useEffect(() => {
    if (isMobile) {
      dispatch(setFooter(false));
    }

    return () => dispatch(setFooter(true));
  }, [dispatch, isMobile]);

  return (
    <AnimateList>
      <Grid
        container
        className={clsx(styles.container, { [styles.paddingTop]: isDialog })}
        direction="column"
        alignItems="center"
      >
        <h1 style={isDialog ? { fontSize: 24 } : {}}>
          {isAffiliate ? 'Affiliate' : `${agency ? 'Agency' : 'Advertiser'}`} sign up
        </h1>

        {agency && (
          <Grid style={{ ...width, marginBottom: 20 }} item xs={11} sm={6} md={4} lg={3} xl={2}>
            <AnimateListItem>
              <h3 style={{ textAlign: 'center' }}>I'm here to buy from</h3>

              <Switch
                firstOption={{ label: 'Models', value: true }}
                secondOption={{ label: 'Non-Models', value: false }}
                setValue={value => setAdult(value)}
                value={adult}
              />
            </AnimateListItem>
          </Grid>
        )}

        {((agency && adult !== 'nothing-selected') || !agency) && (
          <Grid style={width} item xs={11} sm={6} md={4} lg={3} xl={2}>
            <Formik
              validationSchema={schema}
              initialValues={initialValues}
              onSubmit={handleSubmit}
              noValidate
            >
              {({ isSubmiting, errors, values, setFieldValue, handleBlur, touched }) => (
                <Form>
                  <AnimateListItem>
                    <Field
                      component={TextField}
                      name="email"
                      type="email"
                      placeholder={isAffiliate ? 'Email Address' : 'joebloggs@advertiser.com'}
                      variant="outlined"
                      color="primary"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Email width={14} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </AnimateListItem>
                  <AnimateListItem>
                    <Field
                      component={TextField}
                      name="display_name"
                      type="text"
                      placeholder="Display Name (Company Name)"
                      variant="outlined"
                      color="primary"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <User width={14} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </AnimateListItem>

                  <AnimateListItem>
                    <div
                      style={{
                        position: 'relative',
                        display: 'flex',
                        justifyContent: 'space-between',
                      }}
                    >
                      <PhoneInput
                        name="contact_phone"
                        placeholder="Contact Phone"
                        value={values.contact_phone}
                        onBlur={handleBlur}
                        onChange={phone => setFieldValue('contact_phone', phone)}
                        defaultCountry="US"
                        defaultChecked
                        style={{ width: '70%' }}
                        className={clsx(styles.phoneInput, {
                          [styles.phoneInputError]:
                            has(errors, 'contact_phone', false) && touched.contact_phone,
                        })}
                      />

                      <ErrorMessage
                        name="contact_phone"
                        component="span"
                        className={styles.error}
                      />

                      <Field
                        component={TextField}
                        id="search_contact_phone_ext"
                        name="contact_phone_ext"
                        type="text"
                        placeholder="Ext"
                        variant="outlined"
                        color="primary"
                        autoComplete="off"
                        style={{ width: '28%' }}
                      />
                    </div>
                  </AnimateListItem>

                  <PasswordConfirmation />

                  <div
                    style={{ position: 'relative' }}
                    className={errors.terms_of_service && 'checkbox-nested-error'}
                  >
                    <div style={{ width: '100%' }}>
                      <Field
                        component={CheckboxWithLabel}
                        type="checkbox"
                        name="terms_of_service"
                        Label={{ label: 'I agree to the Terms of Service' }}
                        color="primary"
                      />
                    </div>

                    <ErrorMessage
                      name="terms_of_service"
                      component="span"
                      className={styles.error}
                      style={{ top: -5, position: 'relative' }}
                    />
                  </div>

                  <AnimateListItem>
                    <Button
                      variant="contained"
                      color="primary"
                      disabled={isSubmiting || isLoading || !isEmpty(errors)}
                      type="submit"
                      fullWidth
                    >
                      {isLoading ? <CircularProgress color="primary" /> : 'Register'}
                    </Button>
                  </AnimateListItem>
                </Form>
              )}
            </Formik>
          </Grid>
        )}
      </Grid>
    </AnimateList>
  );
};

export default SignUpForm;
